// #1883 — how the health page arranges checks for reading.
//
// ed, 2026-09-10 13:57: he could see pass/fail and nothing else, so a private
// row reading FAIL looked like blanket failure — when the truth was a p95 of
// 5.27 s against a MEDIAN of 0.87 s. Two numbers, one story, and the page showed
// neither.
//
// ed, 13:58: *"if there is a comparison, look between private tg and production
// tg"* — the same fleet and the same database, one target group carrying the
// cache work and one without. Side by side that is the A/B that shows what the
// work bought. Never against stage-, which is a different machine entirely.
//
//   node callsteward-web/tests/assign-health-checks-1883.test.mjs
//
// Pure on purpose: the arranging is the part with rules in it, and rules want
// tests more than markup does.

/** A p95 check and its median sibling: `grid_warm_p95_s` ↔ `grid_warm_p50_s`. */
export function medianNameFor(name) {
    const m = /^(.*)_p95(_s)?$/.exec(String(name || ''));

    return m ? `${m[1]}_p50${m[2] || ''}` : null;
}

/**
 * Display rows for one sample's checks, with each median folded onto its p95 row.
 *
 * ⚠ THE MEDIAN IS NOT DROPPED, it is MOVED. It keeps its own target and its own
 * verdict on the row it joins, because "p95 failed" and "the median is fine" are
 * two facts and the page exists to show both at once. Showing only the worse one
 * is what made a healthy tier read as broken.
 *
 * A p50 with no p95 sibling stays a row of its own — nothing is hidden by being
 * unpaired.
 */
export function pairMedians(checks) {
    const list = Array.isArray(checks) ? checks : [];
    const byName = {};
    list.forEach((c) => { if (c && c.name) byName[c.name] = c; });

    const absorbed = {};
    list.forEach((c) => {
        if (!c || !c.name) return;
        const median = medianNameFor(c.name);
        if (median && byName[median]) absorbed[median] = true;
    });

    return list
        .filter((c) => c && c.name && !absorbed[c.name])
        .map((c) => {
            const medianName = medianNameFor(c.name);

            return { ...c, median: medianName ? byName[medianName] || null : null };
        });
}

/**
 * Is a lower value better for this check?
 *
 * ⚠ IT DEPENDS ON THE OPERATOR, and getting it wrong inverts the meaning of every
 * delta on the hit-ratio row. `<=` targets (seconds, error counts) want less;
 * `>=` targets (hit ratio) want more.
 */
export function lowerIsBetter(op) {
    return String(op || '<=').indexOf('>') !== 0;
}

/**
 * One row per check, one cell per environment, plus the delta.
 *
 * ⚠ THE DELTA IS SIGNED AND ALSO JUDGED. A bare "−3.51" is read as worse by
 * anyone scanning quickly, and on the hit-ratio row it would be the opposite of
 * the truth. Each delta therefore carries `better`, derived from the check's own
 * operator, and the page colours from that rather than from the sign.
 *
 * @param latestByEnv {object} env => record
 * @param envs {string[]} the columns, in order
 */
export function comparisonRows(latestByEnv, envs) {
    const columns = (envs || []).filter((e) => latestByEnv && latestByEnv[e]);
    if (!columns.length) return [];

    // Row order follows the first environment that has checks — the sampler's own
    // order, which is the order the targets table is written in. Sorting them
    // would scramble a list somebody arranged on purpose.
    const order = [];
    const rowsByName = {};

    columns.forEach((env) => {
        pairMedians((latestByEnv[env] || {}).checks).forEach((c) => {
            if (!rowsByName[c.name]) {
                rowsByName[c.name] = { name: c.name, desc: c.desc || null, target: c.target, op: c.op, cells: {} };
                order.push(c.name);
            }
            rowsByName[c.name].cells[env] = c;
        });
    });

    return order.map((name) => {
        const row = rowsByName[name];
        row.delta = deltaFor(row, columns);

        return row;
    });
}

function deltaFor(row, columns) {
    if (columns.length !== 2) return null;

    const a = row.cells[columns[0]];
    const b = row.cells[columns[1]];
    if (!a || !b) return null;

    // ⚠ `Number(null)` is 0 and `isFinite(0)` is true, so an unjudged cell would
    // otherwise produce a delta measured against zero — a claim about a
    // measurement that never happened, rendered as if it had.
    const av = numeric(a.value);
    const bv = numeric(b.value);
    if (av === null || bv === null) return null;

    const diff = av - bv;
    if (diff === 0) return { value: 0, better: null };

    // Positive means the FIRST column is higher. Whether that is better depends
    // on the check, never on the sign.
    return { value: diff, better: lowerIsBetter(row.op) ? diff < 0 : diff > 0 };
}

/** A value that is genuinely a number, or null. `''` and `null` are not zero. */
function numeric(v) {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);

    return isFinite(n) ? n : null;
}
